/* What the coverage matrix says, decided once.
 *
 * **The four states are the entire vocabulary, and none of them is a verdict.** The backend refuses to
 * compute one — Lumiq observes classification, quality, incidents and remediation, while DPDP also
 * requires consent, purpose limitation, data-principal rights and a regulator notification — so the
 * screen's job is to render what was measured and, just as loudly, what was not.
 *
 * **`estate_only` is the one a careless rendering gets wrong.** A control drawing only on the audit
 * chain or on change approvals has nothing per-dataset to report: it is neither satisfied nor missing.
 * Green would be satisfied-by-absence; a gap would send somebody to classify a dataset that could not
 * change the answer. So it takes a neutral tone, a hollow dot and *italic* — a different kind of
 * answer rather than a worse one.
 */

import type {
  ControlCoverage,
  DatasetCoverage,
  EvidenceKind,
} from '@/shared/api/generated/compliance';

/** The states the API publishes, as they arrive. */
export type CoverageState = ControlCoverage['state'];

export interface CoverageTone {
  readonly tone: 'ok' | 'warn' | 'unknown' | 'estate';
  /** Italic for `estate_only` alone — see the note above. */
  readonly italic: boolean;
}

/** Total over the four states, so a fifth is a compile error here rather than a cell rendered in
 * whatever hue a fallback picked. §9 records that exact defect on `verdictTone`, where an unmapped
 * verdict silently took the *permitted* tone. */
export const COVERAGE_TONE = {
  observed: { tone: 'ok', italic: false },
  partial: { tone: 'warn', italic: false },
  not_observed: { tone: 'unknown', italic: false },
  estate_only: { tone: 'estate', italic: true },
} as const satisfies Record<CoverageState, CoverageTone>;

/** The copy key for a cell's label. */
export function coverageLabelKey(state: CoverageState): string {
  return `compliance.coverage.state.${state}`;
}

/** What a cell's hover says: what was observed, what is missing, and what the control answers
 * elsewhere.
 *
 * Assembled from copy keys by the caller rather than concatenated here, because the platform runs ICU
 * and a sentence built by joining translated fragments is a sentence no translator can reorder.
 */
export interface CellDetail {
  readonly key: string;
  readonly observed: string;
  readonly missing: string;
  readonly estate: string;
}

export function cellDetail(control: ControlCoverage): CellDetail {
  return {
    key:
      control.state === 'estate_only'
        ? 'compliance.coverage.detail.estate'
        : control.estate_kinds.length > 0
          ? 'compliance.coverage.detail.mixed'
          : 'compliance.coverage.detail.dataset',
    observed: control.observed.join(', '),
    missing: control.missing.join(', '),
    estate: control.estate_kinds.join(', '),
  };
}

/* ---------------------------------------------------------------- the worklist */

export interface WorklistEntry {
  readonly dataset: string;
  /** How many controls returned *nothing* for this dataset. */
  readonly blankControls: number;
  /** The distinct kinds with no reading, across every control that wants one. */
  readonly missing: readonly EvidenceKind[];
}

export const WORKLIST_SIZE = 5;

/** The datasets with the most unread controls, worst first.
 *
 * **Counted on `not_observed` alone while the kinds come from `partial` too**, and the asymmetry is
 * deliberate rather than an oversight. The count answers "which dataset has Lumiq looked at least",
 * so a control that returned *something* is not a blank; the kind list answers "what would I go and
 * collect", and a partly-read control has an outstanding kind exactly like an unread one.
 *
 * A dataset with no blank controls is absent even if it has partial ones — the panel is a worklist,
 * and a list that includes everything is not one. `estate_only` never contributes: there is nothing
 * per-dataset to collect for it, so counting it would send somebody after evidence that does not
 * attach to a table.
 */
export function worklistFrom(rows: readonly DatasetCoverage[]): readonly WorklistEntry[] {
  return rows
    .map((row) => {
      const missing = new Set<EvidenceKind>();
      let blankControls = 0;
      for (const control of row.controls) {
        if (control.state === 'not_observed') blankControls += 1;
        if (control.state === 'not_observed' || control.state === 'partial') {
          for (const kind of control.missing) missing.add(kind);
        }
      }
      return {
        dataset: row.qualified_name,
        blankControls,
        missing: [...missing],
      };
    })
    .filter((entry) => entry.blankControls > 0)
    .sort(
      (left, right) =>
        right.blankControls - left.blankControls ||
        left.dataset.localeCompare(right.dataset),
    )
    .slice(0, WORKLIST_SIZE);
}
